import { CheckIcon, MinusIcon } from 'lucide-react';
import {
  alleen,
  mag,
  RECHTENSOORTEN,
  RECHTEN_LABEL,
  RECHTEN_OMSCHRIJVING,
  RECHTEN_OVERZICHT,
} from '@vip/shared';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Item, ItemContent, ItemDescription, ItemGroup, ItemTitle } from '@/components/ui/item';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * Wat elk vinkje oplevert, afgeleid uit de rechtentabel zelf. Wie meerdere
 * vinkjes heeft mag alles wat er in die kolommen staat.
 *
 * Dit stond onder Personen, op het tabblad Uitnodigingen. Daar hoorde het niet:
 * het gaat niet over uitnodigen, en het is naslag en geen werk. Naslag hoort bij
 * de instellingen, achter het recht van degene die de vinkjes ook zet.
 *
 * De tabel wordt opgebouwd uit `RECHTEN_OVERZICHT` en `mag`, en niet uit een
 * met de hand bijgehouden lijstje. Dat het overzicht compleet blijft staat in
 * `rechten.test.ts`: een tabel die uit de pas loopt met de werkelijke rechten is
 * erger dan geen tabel.
 */
export function RechtenPaneel() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Wat een vinkje oplevert</CardTitle>
        <CardDescription>
          Ligt vast in de code; verandert alleen met een nieuwe versie.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead />
                {RECHTENSOORTEN.map((soort) => (
                  <TableHead className="text-center" key={soort}>
                    {RECHTEN_LABEL[soort]}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {RECHTEN_OVERZICHT.map(({ recht, label }) => (
                <TableRow key={recht}>
                  <TableCell className="font-medium">{label}</TableCell>
                  {RECHTENSOORTEN.map((soort) => {
                    const toegestaan = mag(alleen(soort), recht);
                    return (
                      <TableCell className="text-center" key={soort}>
                        {toegestaan ? (
                          <CheckIcon className="inline size-4 text-primary" />
                        ) : (
                          <MinusIcon className="inline size-4 text-muted-foreground/50" />
                        )}
                        <span className="sr-only">
                          {RECHTEN_LABEL[soort]}: {toegestaan ? 'wel' : 'niet'}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <ItemGroup>
          {RECHTENSOORTEN.map((soort) => (
            <Item key={soort} size="sm" variant="muted">
              <ItemContent>
                <ItemTitle>{RECHTEN_LABEL[soort]}</ItemTitle>
                <ItemDescription>{RECHTEN_OMSCHRIJVING[soort]}</ItemDescription>
              </ItemContent>
            </Item>
          ))}
        </ItemGroup>
      </CardContent>
    </Card>
  );
}
